import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import {
  ArtifactStatusChip,
  ProjectDetailHeader,
  type PageHeaderBreadcrumb,
  type ProjectDetailHeaderMetaItem,
} from '@verantos-dev/shared-components';

/**
 * Shared concept-group asset header. Both the Draft review and the Ready detail view
 * render it, so the two surfaces read identically: breadcrumb, name title, a status
 * chip (``Draft`` / ``Ready``, auto-coloured by ArtifactStatusChip), the condensed meta
 * row, and an optional right-aligned action. The Draft passes Save as ``rightContent``;
 * the Ready view passes none (read-only).
 *
 * Composes {@link ProjectDetailHeader} and builds the title row here rather than going
 * through ArtifactDetailHeader, because the Draft's title is an inline name editor and
 * the shared component renders a plain string.
 */
export const ConceptGroupAssetHeader = ({
  id = 'cg-preview-header',
  breadcrumbs,
  title,
  status,
  isLoading,
  metaItems,
  rightContent,
  titleContent,
  titleTestId = 'cg-preview-title',
  statusTestId = 'cg-preview-status',
}: {
  id?: string;
  breadcrumbs?: PageHeaderBreadcrumb[];
  title: string;
  /** ``Draft`` / ``Ready`` — omit to hide the chip. */
  status?: string;
  isLoading?: boolean;
  metaItems: ProjectDetailHeaderMetaItem[];
  rightContent?: ReactNode;
  /** Replaces the rendered title — the Draft passes its inline name editor. */
  titleContent?: ReactNode;
  titleTestId?: string;
  statusTestId?: string;
}) => {
  const titleSlot = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      {titleContent ?? (
        <Typography
          variant="h5"
          component="h1"
          data-testid={titleTestId}
          sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {title}
        </Typography>
      )}
      {status && <ArtifactStatusChip status={status} data-testid={statusTestId} />}
    </Box>
  );

  return (
    <ProjectDetailHeader
      id={id}
      breadcrumbs={breadcrumbs}
      title={title}
      isTitleLoading={isLoading}
      titleSlot={isLoading ? undefined : titleSlot}
      rightContent={rightContent}
      metaItems={metaItems}
    />
  );
};
